/**
 * Billable Nodes Utilities
 * 
 * Identifies which nodes actually consume credits (billable nodes)
 * vs. utility nodes that don't generate content.
 */

import { Node } from '@xyflow/react';

/**
 * Node types that consume credits when executed
 */
const BILLABLE_NODE_TYPES = new Set([
  'agentPrompt',
  'geminiText',
  'grokText',
  'imagePrompt',
  'fluxImage',
  'zImageTurbo',
  'qwenImage',
  'qwenImageEdit',
  'seedream',
  'recraftV3',
  'klingVideo',
  'kling26Video',
  'kling25Video',
  'wanVideo',
  'wan26Video',
  'wanAnimateVideo',
  'ltxVideo',
  'ltxFastVideo',
  'minimaxVideo',
  'pixVerseVideo',
  'hunyuanVideo',
  'klingAvatar',
  'veoVideo',
  'geminiOmni',
  'seedance2Video',
  'seedance25Video',
  'minimaxH3Video',
  'klingO3Video',
  'grokImage',
  'grokVideo',
  'glmImage',
  'klingImage',
  'gptImage',
  'inpaint',
  // ElevenLabs audio generators. Omitting them made every audio node contribute
  // 0 to the selection and whole-flow totals while the server charged up to
  // 46 credits per 1,000 characters of speech.
  'voiceTts',
  'soundEffects',
  'music'
]);

/**
 * Check if a node type is billable (consumes credits)
 */
export function isBillableNodeType(nodeType: string | undefined): boolean {
  if (!nodeType) return false;
  return BILLABLE_NODE_TYPES.has(nodeType);
}

/**
 * Get all billable (credit-consuming) nodes
 */
export function getBillableNodes(nodes: Node[]): Node[] {
  return nodes.filter(node => isBillableNodeType(node.type));
}

/**
 * Get count of billable nodes that will actually generate content
 */
export function getBillableNodeCount(nodes: Node[]): number {
  return getBillableNodes(nodes).length;
}
